import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen } from 'lucide-react';
import { useCourses, queryKeys } from '@/api/hooks';
import { api } from '@/api/client';
import { CourseCard, CourseCategoryFilter } from '@/components/learning';
import type { Course } from '@/api/client';
import { Skeleton } from '@/components/ui/skeleton';
import { useQueryClient } from '@tanstack/react-query';

function CoursesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);

  // Fetched UNFILTERED — RLS scopes it to the viewer's entitlement. Filtering by
  // pillar in memory lets the pill rail grey out pillars with no accessible courses
  // (e.g. Sales on WunderPack) instead of dropping the user on an empty grid.
  const { data: allCourses = [], isLoading } = useCourses();

  const availablePillars = useMemo(
    () =>
      isLoading ? null : new Set(allCourses.map((c) => c.pillar).filter((p): p is string => !!p)),
    [allCourses, isLoading],
  );

  const courses = useMemo(
    () => (selectedCategory ? allCourses.filter((c) => c.pillar === selectedCategory) : allCourses),
    [allCourses, selectedCategory],
  );

  // A pillar with no accessible courses is disabled; if one was already selected
  // when the set resolved, fall back to All rather than showing an empty grid.
  useEffect(() => {
    if (availablePillars && selectedCategory && !availablePillars.has(selectedCategory)) {
      setSelectedCategory(undefined);
    }
  }, [availablePillars, selectedCategory]);

  const handleCourseClick = (course: Course) => {
    navigate({ to: '/learn/courses/$courseId', params: { courseId: course.id } });
  };

  // Prefetch course detail on hover — data is cached before the user clicks
  const handleCourseHover = (courseId: string) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.course(courseId),
      queryFn: () => api.getCourse(courseId),
    });
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-foreground mb-2">Courses</h1>
        <p className="text-muted-foreground mb-6">Browse and enroll in courses</p>

        <CourseCategoryFilter
          value={selectedCategory}
          onChange={setSelectedCategory}
          availablePillars={availablePillars}
        />
      </motion.div>

      {/* Loading State — skeleton grid */}
      {isLoading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-card rounded-xl border border-border overflow-hidden">
              <Skeleton className="h-40 w-full" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <div className="flex gap-3 pt-1">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Course Grid */}
      {!isLoading && courses.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course, idx) => (
            <div
              key={course.id}
              onMouseEnter={() => handleCourseHover(course.id)}
            >
              <CourseCard
                course={course}
                index={idx}
                onClick={() => handleCourseClick(course)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && courses.length === 0 && (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Courses Available</h3>
          <p className="text-muted-foreground">
            {selectedCategory
              ? `No courses found in the ${selectedCategory} category.`
              : 'Courses will appear here once they are published.'}
          </p>
          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory(undefined)}
              className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium transition-colors"
            >
              View All Courses
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/learn/courses/')({
  component: CoursesPage,
});
